import {
	useApiGetMonitorMetrics,
	useApiGetMonitorTimeseries,
	type ApiGetMonitorTimeseriesVariables,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { keepPreviousData } from '@tanstack/react-query';
import { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { AnalyticsBanner } from './components/AnalyticsBanner';
import { MetricsMonitorCards } from './components/cards/MetricsMonitorCards';
import { MonitorsOverviewChart } from './components/charts/MonitorsOverviewChart';
import { MetricsOverviewTab } from './components/MetricsOverviewTab';
import { MetricsOverviewTabSkeleton } from './components/MetricsOverviewTabSkeleton';
import { monitorsFilterOptions } from './constants';
import { useOverviewMetricDatetime } from './hooks/useMetricOverviewDatetime';
import type { FilterType, GenericOverviewTabControllerProps } from './types';

import { Box } from '@mantine/core';
import { isValidTimeRange, isValidViewOption } from './types.ts';
export const MonitorsOverviewTabController = memo(
	({ onMetricCardClick }: GenericOverviewTabControllerProps) => {
		const { user, workspace } = useAuthUser();
		const {
			timeRangeValue,
			setTimeRangeValue,
			dateRangeOption,
			setDateRangeOption,
			dateFilter,
			setDateFilter,
		} = useOverviewMetricDatetime({ cacheKey: 'monitors' });
		const navigate = useNavigate();
		const [filterType, setFilterType] = useState<FilterType>('all');

		// Create wrapper functions to handle type conversion with validation
		const handleSetDateFilter = useCallback(
			(value: string) => {
				if (isValidTimeRange(value)) {
					setDateFilter(value);
				}
			},
			[setDateFilter]
		);

		const handleSetDateRangeOption = useCallback(
			(value: string) => {
				if (isValidViewOption(value)) {
					setDateRangeOption(value);
				}
			},
			[setDateRangeOption]
		);

		const { data: metrics, isLoading: isMetricsLoading } =
			useApiGetMonitorMetrics({});

		const { data: timeseries, isPending: isTimeseriesLoading } =
			useApiGetMonitorTimeseries(
				{
					queryParams: {
						filter_type: filterType,
						time_range: dateFilter,
						view_option: dateRangeOption,
					},
				} as ApiGetMonitorTimeseriesVariables,
				{ placeholderData: keepPreviousData }
			);

		const handleFilterTypesChange = useCallback(
			(filterTypes: string[]) => {
				const newFilterType = filterTypes[0] as FilterType;
				setFilterType(newFilterType);

				const filterLabel = monitorsFilterOptions.find(
					(opt) => opt.value === newFilterType
				)?.label;
				if (!filterLabel) return;

				trackEvent(
					'monitors/overview/filter_change',
					{
						filter_type: filterLabel,
						filter_value: newFilterType,
					},
					user,
					workspace!
				);
			},
			[user, workspace]
		);

		const handleNavigate = useCallback(() => {
			navigate(`/analytics`);
		}, [navigate]);

		if (isMetricsLoading || isTimeseriesLoading || !timeseries || !metrics) {
			return <MetricsOverviewTabSkeleton />;
		}

		return (
			<Box pb="xl">
				<MetricsOverviewTab
					filterOptions={[monitorsFilterOptions]}
					filterTypes={[filterType]}
					setFilterTypes={handleFilterTypesChange}
				>
					<MetricsMonitorCards
						monitorMetrics={metrics}
						onMetricCardClick={onMetricCardClick}
					/>
					<AnalyticsBanner handleNavigate={handleNavigate} />
					<MonitorsOverviewChart
						timeseries={timeseries}
						dateFilter={dateFilter || '24h'}
						setDateFilter={handleSetDateFilter}
						setTimeRangeValue={setTimeRangeValue}
						timeRangeValue={timeRangeValue}
						dateRangeOption={dateRangeOption || 'day'}
						setDateRangeOption={handleSetDateRangeOption}
					/>
				</MetricsOverviewTab>
			</Box>
		);
	}
);
